/**
 * AuditIntegrityPanel — Tamper-evident audit chain viewer & verifier.
 *
 * Phase 1.7 — Audit Chain Surface (redesign-p1-1.7-audit-chain)
 *
 * Displays the cryptographic hash chain for a report and provides
 * integrity verification. Each entry shows block index, timestamp,
 * SHA-256 hash, and link to previous hash.
 *
 * Design spec: docs/plans/2026-06-25-redesign-design-spec.md
 *   - Orange (#FF6600) = brand/signal only (CTA button)
 *   - Green (#22C55E) = done/intact only
 *   - Near-black surfaces (#0A0A0A → #121212 → #161616)
 *   - DM Mono for hashes/timestamps, Outfit for UI, DM Serif for headings
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Shield, ShieldCheck, ShieldAlert, RefreshCw } from 'lucide-react';
import { DataTable, Column } from '@/components/ui/DataTable';
import { Button } from '@/components/ui/Button';
import { EmptyState } from '@/components/ui/EmptyState';
import { cn } from '@/lib/utils';
import { api } from '../../utils/api';

// -----------------------------------------------------------------------------
// Types
// -----------------------------------------------------------------------------

interface AuditChainEntry {
  block_index: number;
  timestamp: string;
  hash: string;
  prev_hash: string | null;
  action?: string;
  actor?: string;
}

interface AuditChainData {
  report_id: string;
  entries: AuditChainEntry[];
  count: number;
}

interface VerifyResult {
  report_id: string;
  result: 'intact' | 'broken';
  details?: string;
  broken_at_index?: number;
}

// -----------------------------------------------------------------------------
// Props Interface
// -----------------------------------------------------------------------------

export interface AuditIntegrityPanelProps {
  reportId: string;
  className?: string;
}

// -----------------------------------------------------------------------------
// Status Badge Component (Design Spec §3)
// -----------------------------------------------------------------------------

type VerifyStatus = 'intact' | 'broken' | 'pending' | 'error';

interface VerifyStatusBadgeProps {
  status: VerifyStatus;
  className?: string;
}

function VerifyStatusBadge({ status, className }: VerifyStatusBadgeProps) {
  const statusConfig: Record<VerifyStatus, { text: string; icon: React.ReactNode; styles: string }> = {
    intact: {
      text: 'Intact',
      icon: <ShieldCheck className="h-3.5 w-3.5" />,
      styles: 'bg-transparent text-[var(--color-verified)] border-[rgba(34,197,94,0.30)]',
    },
    broken: {
      text: 'Broken',
      icon: <ShieldAlert className="h-3.5 w-3.5" />,
      styles: 'bg-[var(--color-needs-human-tint)] text-[var(--color-brand)] border-[var(--color-needs-human-tint-bd)]',
    },
    pending: {
      text: 'Verify',
      icon: <Shield className="h-3.5 w-3.5" />,
      styles: 'bg-[var(--color-bg-elevated)] text-[var(--color-text-muted)] border-[var(--color-border-sub)]',
    },
    error: {
      text: 'Error',
      icon: <ShieldAlert className="h-3.5 w-3.5" />,
      styles: 'bg-[var(--color-needs-human-tint)] text-[var(--color-brand)] border-[var(--color-needs-human-tint-bd)]',
    },
  };

  const config = statusConfig[status];

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold',
        config.styles,
        className
      )}
    >
      {config.icon}
      {config.text}
    </span>
  );
}

// -----------------------------------------------------------------------------
// Monospace Value Display (DM Mono for hashes)
// -----------------------------------------------------------------------------

interface MonoValueProps {
  children: React.ReactNode;
  className?: string;
  truncate?: boolean;
  /** Native tooltip — used to show the full hash when the display value is truncated. */
  title?: string;
}

function MonoValue({ children, className, truncate = false, title }: MonoValueProps) {
  return (
    <span
      title={title}
      className={cn(
        'font-mono text-sm tabular-nums',
        truncate && 'truncate block max-w-[200px]',
        className
      )}
      style={{ fontFamily: 'DM Mono, ui-monospace, monospace' }}
    >
      {children}
    </span>
  );
}

// -----------------------------------------------------------------------------
// Hash Cell Renderer (truncated with full title)
// -----------------------------------------------------------------------------

function HashCell({ hash }: { hash: string }) {
  const displayHash = hash.length > 16 ? `${hash.slice(0, 8)}…${hash.slice(-8)}` : hash;

  return (
    <MonoValue
      className="text-[var(--color-text-secondary)]"
      truncate
      title={hash}
    >
      {displayHash}
    </MonoValue>
  );
}

// -----------------------------------------------------------------------------
// Timestamp Cell Renderer
// -----------------------------------------------------------------------------

function TimestampCell({ timestamp }: { timestamp: string }) {
  const date = new Date(timestamp);
  const formatted = date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

  return (
    <MonoValue className="text-[var(--color-text-muted)] text-xs">
      {formatted}
    </MonoValue>
  );
}

// -----------------------------------------------------------------------------
// Main Component
// -----------------------------------------------------------------------------

export function AuditIntegrityPanel({ reportId, className }: AuditIntegrityPanelProps) {
  const { reportId: reportIdProp } = { reportId };
  const effectiveReportId = reportIdProp;
  const [data, setData] = useState<AuditChainData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [verifyResult, setVerifyResult] = useState<VerifyResult | null>(null);
  const [verifying, setVerifying] = useState(false);
  const [verifyError, setVerifyError] = useState<string | null>(null);

  const fetchChain = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await api.get<AuditChainData>(
        `/api/v1/audit-chain/${effectiveReportId}/chain`,
        { showErrorToast: false }
      );
      setData(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  }, [effectiveReportId]);

  const handleVerify = useCallback(async () => {
    setVerifying(true);
    setVerifyError(null);
    setVerifyResult(null);
    try {
      const result = await api.get<VerifyResult>(
        `/api/v1/audit-chain/${effectiveReportId}/verify`,
        { showErrorToast: false }
      );
      setVerifyResult(result);
    } catch (err) {
      setVerifyError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setVerifying(false);
    }
  }, [effectiveReportId]);

  useEffect(() => {
    fetchChain();
  }, [fetchChain]);

  // Table columns definition
  const columns: Column<AuditChainEntry>[] = [
    {
      key: 'block_index',
      label: 'Block',
      sortable: true,
      width: '80px',
      render: (value: number) => (
        <MonoValue className="font-medium text-[var(--color-text-primary)]">
          #{value}
        </MonoValue>
      ),
    },
    {
      key: 'timestamp',
      label: 'Timestamp',
      sortable: true,
      width: '140px',
      render: (value: string) => <TimestampCell timestamp={value} />,
    },
    {
      key: 'hash',
      label: 'SHA-256 Hash',
      sortable: false,
      width: '220px',
      render: (value: string) => <HashCell hash={value} />,
    },
    {
      key: 'prev_hash',
      label: 'Previous Hash',
      sortable: false,
      width: '220px',
      render: (value: string | null) =>
        value ? <HashCell hash={value} /> : (
          <MonoValue className="text-[var(--color-text-ghost)]">
            —
          </MonoValue>
        ),
    },
    {
      key: 'action',
      label: 'Action',
      sortable: false,
      width: '120px',
      render: (value: string | undefined) => (
        <span className="text-xs text-[var(--color-text-muted)] capitalize">
          {value || '—'}
        </span>
      ),
    },
  ];

  // Loading state
  if (loading) {
    return (
      <div
        className={cn(
          'rounded-xl border border-[var(--color-border)] bg-[var(--color-bg-surface)] p-6',
          className
        )}
      >
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="h-6 w-6 animate-spin text-[var(--color-brand)]" />
          <span className="ml-3 text-sm text-[var(--color-text-secondary)]">
            Loading audit chain…
          </span>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div
        className={cn(
          'rounded-xl border border-[var(--color-border)] bg-[var(--color-bg-surface)] p-6',
          className
        )}
      >
        <div className="flex items-center gap-3 rounded-lg bg-[rgba(255,102,0,0.08)] p-4">
          <ShieldAlert className="h-5 w-5 text-[var(--color-brand)]" />
          <div className="flex-1">
            <p className="text-sm font-medium text-[var(--color-text-primary)]">
              Failed to load audit chain
            </p>
            <p className="text-xs text-[var(--color-text-muted)]">{error}</p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={fetchChain}
            className="border-[var(--color-brand)] text-[var(--color-brand)] hover:bg-[var(--color-brand)] hover:text-[var(--color-on-brand)]"
          >
            Retry
          </Button>
        </div>
      </div>
    );
  }

  // Empty state
  if (!data || data.count === 0) {
    return (
      <div
        className={cn(
          'rounded-xl border border-[var(--color-border)] bg-[var(--color-bg-surface)]',
          className
        )}
      >
        <EmptyState
          icon={<Shield className="h-8 w-8" />}
          title="No audit chain entries"
          description="This report does not have any audit trail entries yet."
          className="py-12"
        />
      </div>
    );
  }

  // Determine verification status for display
  let verifyStatus: VerifyStatus = 'pending';
  if (verifying) {
    verifyStatus = 'pending';
  } else if (verifyError) {
    verifyStatus = 'error';
  } else if (verifyResult) {
    verifyStatus = verifyResult.result;
  }

  return (
    <div
      className={cn(
        'rounded-xl border border-[var(--color-border)] bg-[var(--color-bg-surface)]',
        className
      )}
      style={{ backgroundColor: 'var(--color-bg-surface)' }}
    >
      {/* Header */}
      <div className="flex items-center justify-between border-b border-[var(--color-border-sub)] px-5 py-4">
        <div>
          <h2
            className="text-lg font-semibold"
            style={{
              fontFamily: 'DM Serif Display, Georgia, serif',
              color: 'var(--color-text-primary)',
            }}
          >
            Verify Integrity
          </h2>
          <p className="mt-0.5 text-xs text-[var(--color-text-muted)]">
            {data.count} entries • Cryptographic hash chain
          </p>
        </div>
        <div className="flex items-center gap-3">
          <VerifyStatusBadge status={verifyStatus} />
          <Button
            size="sm"
            onClick={handleVerify}
            disabled={verifying}
            className="gap-2 bg-[var(--color-brand)] text-[var(--color-on-brand)] hover:bg-[var(--color-brand-hover)]"
          >
            {verifying ? (
              <RefreshCw className="h-4 w-4 animate-spin" />
            ) : (
              <Shield className="h-4 w-4" />
            )}
            {verifying ? 'Verifying…' : 'Verify integrity'}
          </Button>
        </div>
      </div>

      {/* Verification error */}
      {verifyError && (
        <div className="border-b border-[var(--color-border-sub)] bg-[rgba(255,102,0,0.04)] px-5 py-3">
          <p className="text-xs text-[var(--color-text-muted)]">
            <span className="font-medium text-[var(--color-brand)]">Verification error:</span>{' '}
            {verifyError}
          </p>
        </div>
      )}

      {/* Verification details for broken chain */}
      {verifyResult?.result === 'broken' && verifyResult.details && (
        <div className="border-b border-[var(--color-border-sub)] bg-[rgba(255,102,0,0.08)] px-5 py-3">
          <p className="text-xs text-[var(--color-text-secondary)]">
            <span className="font-medium text-[var(--color-brand)]">Chain broken:</span>{' '}
            {verifyResult.details}
            {verifyResult.broken_at_index !== undefined && (
              <span className="ml-1 font-mono text-[var(--color-text-muted)]">
                at block #{verifyResult.broken_at_index}
              </span>
            )}
          </p>
        </div>
      )}

      {/* Data Table */}
      <div className="p-0">
        <DataTable
          columns={columns}
          data={data.entries}
          emptyMessage="No audit chain entries"
          className="border-0"
        />
      </div>
    </div>
  );
}

export default AuditIntegrityPanel;
